import { useMemo, useState } from 'react'
import { Link, useLocation, useParams } from '#router'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ARCHIVE_GROUP_LABELS,
  ARCHIVE_GROUP_STAGE,
  ARCHIVE_GROUPS,
  type ArchiveGroup,
  type QueueSort,
  toArchiveGroup,
} from '@/lib/audition/queue'
import type { AuditionStage } from '@/lib/audition/state-machine'
import { trpc } from '@/lib/trpc'
import {
  useArchiveGroup,
  useDebouncedValue,
  useReviewLens,
} from './hooks/use-auditions'
import {
  type ArchiveOrigin,
  archiveHref,
  ControlBand,
  FilterChip,
  LaneBand,
  type LaneFilter,
  LensBand,
  QUEUE_HREF,
  QueueRowItem,
  SearchBox,
  SortBand,
} from './queue-parts'

/**
 * How long the admin stops typing before the server is asked. Every distinct
 * value is a keyset read plus a `count()` over the table this ticket exists
 * to stop scanning, so typing a name unthrottled would issue ten of them.
 */
const ARCHIVE_SEARCH_DEBOUNCE_MS = 300

/** The archive the bare URL opens. Named, not `ARCHIVE_GROUPS[0]`. */
const DEFAULT_ARCHIVE: ArchiveGroup = 'not_yet_submitted'

/**
 * What the archive is doing right now, as ONE answer (LG-914).
 *
 * The queue page once combined a group's `isPending`, `isError`,
 * `isPlaceholderData` and the debounce by hand at six sites, and six review
 * rounds each found one site missing one flag. Every consumer on this page
 * reads this instead. First match wins, top to bottom:
 *
 *  - `loading`: first fetch, nothing to show yet
 *  - `error`:   the fetch failed; whatever rows exist are kept, with a banner
 *  - `stale`:   the rows answer a PREVIOUS search — the box has changed and
 *               the debounce has not fired, or the key has changed and
 *               react-query is holding the old page up
 *  - `ready`:   the rows are the answer to the current controls
 */
type ArchiveStatus = 'loading' | 'error' | 'stale' | 'ready'

function archiveStatus(
  archive: Pick<
    ReturnType<typeof useArchiveGroup>,
    'isPending' | 'isError' | 'isPlaceholderData'
  >,
  debouncing: boolean,
): ArchiveStatus {
  if (archive.isPending) return 'loading'
  if (archive.isError) return 'error'
  if (archive.isPlaceholderData || debouncing) return 'stale'
  return 'ready'
}

/**
 * The three archives as a row of links, the current one marked. Links rather
 * than buttons because the group IS the URL: bookmarkable, back-button-able,
 * and where the queue page's strip lands.
 */
function ArchiveNav({
  current,
  stageCounts,
  known,
}: {
  current: ArchiveGroup | null
  stageCounts: Partial<Record<AuditionStage, number>>
  known: boolean
}) {
  return (
    <nav aria-label="Archives" className="flex flex-wrap items-center gap-2">
      {ARCHIVE_GROUPS.map((group) => {
        const active = group === current
        return (
          <Link
            key={group}
            href={archiveHref(group)}
            aria-current={active ? 'page' : undefined}
            className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${
              active
                ? 'border-primary bg-primary/10 text-foreground'
                : 'border-border bg-card text-muted-foreground hover:border-primary/50'
            }`}
          >
            {ARCHIVE_GROUP_LABELS[group]}
            {/* Unknown is not zero: no number until `laneMix` has answered. */}
            {known && (
              <span className="ml-1 opacity-70">
                {stageCounts[ARCHIVE_GROUP_STAGE[group]] ?? 0}
              </span>
            )}
          </Link>
        )
      })}
    </nav>
  )
}

/**
 * One archive of applicants, a page at a time (LG-914).
 *
 * The archives — not yet submitted, accepted, rejected — are the half of the
 * table that grows with marketing traffic rather than with hiring, so they
 * are never read whole. This page reads exactly one group by keyset cursor,
 * with every filter a SQL predicate can express applied on the server so a
 * filter means the same thing on page one as on page nine. The Call facet
 * is deliberately absent: it is derived from the proposal and booking
 * history, and `browse` refuses to re-derive it in SQL.
 */
export function AuditionArchive() {
  const params = useParams()
  const location = useLocation()
  const group =
    params.group === undefined ? DEFAULT_ARCHIVE : toArchiveGroup(params.group)

  const [sort, setSort] = useState<QueueSort>('longest_waiting')
  const [lens, setLens] = useReviewLens()
  // Seeded once from `?q=`: the queue page sends a search that matched nobody
  // in the pipeline here with the query attached.
  const [search, setSearch] = useState(
    () => location.searchParams.get('q') ?? '',
  )
  const [laneFilter, setLaneFilter] = useState<LaneFilter | null>(null)
  const [paidOnly, setPaidOnly] = useState(false)

  const settledSearch = useDebouncedValue(
    search.trim(),
    ARCHIVE_SEARCH_DEBOUNCE_MS,
  )
  const debouncing = search.trim() !== settledSearch

  const laneMix = trpc.admin.auditions.laneMix.useQuery()
  const stageCounts = useMemo(() => {
    const counts: Partial<Record<AuditionStage, number>> = {}
    for (const entry of laneMix.data?.byStage ?? [])
      counts[entry.stage] = entry.count
    return counts
  }, [laneMix.data])

  const archive = useArchiveGroup({
    // A URL naming no archive reads nothing; the hook still has to be called.
    group: group ?? DEFAULT_ARCHIVE,
    enabled: group !== null,
    sort,
    search: settledSearch,
    lane: laneFilter,
    paidOnly,
  })

  // The way back each row carries to the detail page (LG-1052), derived from
  // what the ROWS answer rather than from what the controls now say.
  //
  // The settled search, not the keystroke — and not while react-query is
  // holding the previous key's page up either. `group` flips on the click in
  // the Archives nav, and `settledSearch` on the debounce, but the rows on
  // screen are the old group's (or the old search's) until the new page
  // lands; a `from` built from the new pair sends the admin back to a list
  // the row they clicked is not in. No way back beats a wrong one: those
  // rows fall back to the queue for the moment they are stale.
  //
  // One object per change, because `QueueRowItem` is memoized on it.
  const answering = !archive.isPlaceholderData
  const origin = useMemo<ArchiveOrigin | undefined>(
    () =>
      group && answering ? { group, q: settledSearch || undefined } : undefined,
    [group, settledSearch, answering],
  )

  if (group === null) {
    return (
      <div className="mx-auto max-w-5xl space-y-4 p-6 text-center">
        <h2 className="text-lg font-semibold">No such archive</h2>
        <p className="text-muted-foreground mx-auto max-w-md text-sm">
          Pick one of the three:
        </p>
        <div className="flex justify-center">
          <ArchiveNav
            current={null}
            stageCounts={stageCounts}
            known={laneMix.data !== undefined}
          />
        </div>
      </div>
    )
  }

  const title = ARCHIVE_GROUP_LABELS[group]
  const status = archiveStatus(archive, debouncing)
  const busy = status === 'loading' || status === 'stale'
  // Only for a group that actually paged; `pageCount` says that without a
  // second copy of the server's page size.
  const paging = archive.hasNextPage || archive.pageCount > 1
  // `hasNextPage` is the PREVIOUS key's cursor while a new search is in
  // flight, so a click then would page a different search or do nothing.
  const canPage =
    status === 'ready' && archive.hasNextPage && !archive.isFetchingNextPage

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-6">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-2xl font-semibold">Archive</h1>
        <Link
          href={QUEUE_HREF}
          className="text-muted-foreground text-sm underline-offset-4 hover:underline"
        >
          ← Back to the queue
        </Link>
      </div>

      {laneMix.isError && (
        <p
          role="alert"
          className="border-destructive/50 bg-destructive/5 flex flex-wrap items-center gap-2 rounded-md border p-3 text-sm"
        >
          Couldn't load the archive counts.
          <Button
            variant="link"
            className="h-auto p-0"
            onClick={() => laneMix.refetch()}
          >
            Retry
          </Button>
        </p>
      )}

      <ArchiveNav
        current={group}
        stageCounts={stageCounts}
        known={laneMix.data !== undefined}
      />

      <LaneBand lane={laneFilter} onChange={setLaneFilter} />

      <div className="flex flex-wrap items-start gap-x-10 gap-y-5">
        <ControlBand label="Pipeline facts">
          <FilterChip
            active={paidOnly}
            onClick={() => setPaidOnly((prev) => !prev)}
          >
            Paid
          </FilterChip>
        </ControlBand>
        <SortBand sort={sort} onChange={setSort} />
        <ControlBand label="Show">
          <SearchBox value={search} onChange={setSearch} />
        </ControlBand>
        <LensBand lens={lens} onChange={setLens} className="ml-auto" />
      </div>

      {/* One section, one status. Dimmed and busy for `stale` so briefly-old
          rows never read as the answer; busy for `loading` too. */}
      <section
        aria-labelledby="audition-archive-heading"
        aria-busy={busy || undefined}
        className={`space-y-1 transition-opacity ${status === 'stale' ? 'opacity-60' : ''}`}
      >
        <div className="flex items-baseline gap-2">
          <h2
            id="audition-archive-heading"
            className="text-muted-foreground text-sm font-semibold uppercase tracking-wide"
          >
            {title}
          </h2>
          {/* Page one carries the SQL count for the current filters; until it
              lands there is no number, because unknown is not zero. */}
          {archive.total !== null && (
            <span className="text-muted-foreground text-xs">
              {archive.total}
            </span>
          )}
        </div>

        {status === 'loading' && (
          <div className="space-y-3">
            <p className="text-muted-foreground text-xs">Loading…</p>
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} className="h-14 w-full" />
            ))}
          </div>
        )}

        {status === 'error' && (
          <p
            role="alert"
            className="text-destructive flex flex-wrap items-center gap-2 text-sm"
          >
            {`Couldn't load ${title.toLowerCase()}.`}
            <Button
              variant="link"
              className="h-auto p-0 text-sm"
              onClick={() => archive.refetch()}
            >
              Retry
            </Button>
          </p>
        )}

        {archive.rows.length > 0 && (
          <ul className="divide-border divide-y rounded-md border">
            {archive.rows.map((row) => (
              <QueueRowItem key={row.id} row={row} lens={lens} from={origin} />
            ))}
          </ul>
        )}

        {status === 'ready' && archive.rows.length === 0 && (
          <p className="text-muted-foreground py-10 text-center text-sm">
            {`Nobody in ${title.toLowerCase()} matches the current filters.`}{' '}
            <Button
              variant="link"
              onClick={() => {
                setSearch('')
                setLaneFilter(null)
                setPaidOnly(false)
              }}
            >
              Clear filters
            </Button>
          </p>
        )}

        {paging && (
          <>
            {/* Stays mounted after the last page, saying so, rather than
                vanishing: this is the only paging control and the admin is
                holding it at exactly the moment `hasNextPage` goes false, so
                unmounting drops keyboard focus to the top of the document.
                `aria-disabled`, not `disabled`, for the same reason — which
                makes the handler's own guard the thing that stops the read. */}
            <Button
              variant="link"
              className="h-auto p-0 text-xs aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              aria-disabled={!canPage}
              aria-label={
                archive.isFetchingNextPage
                  ? `Loading more… — ${title}`
                  : archive.hasNextPage
                    ? `Show more — ${title}`
                    : `No more — ${title}`
              }
              onClick={() => {
                if (canPage) archive.fetchNextPage()
              }}
            >
              {archive.isFetchingNextPage
                ? 'Loading more…'
                : archive.hasNextPage
                  ? 'Show more'
                  : 'No more'}
            </Button>
            {/* Mounted with the button and empty until there is an outcome: a
                live region inserted in the same commit as its text is not
                reliably announced. It carries only what the button's name
                cannot — how many rows landed below the focus. */}
            <p className="sr-only" role="status">
              {!archive.isFetchingNextPage && archive.pageCount > 1
                ? `Showing ${archive.rows.length} — ${title}${
                    archive.hasNextPage ? '' : ', no more to load'
                  }`
                : ''}
            </p>
          </>
        )}
      </section>
    </div>
  )
}
